
import { Options, Vue } from 'vue-class-component';
import { roadmapService } from '@/services/roadmap.service';
import { TimelineEvent, RoadmapData } from '@/interfaces/timeline';

import TimelineMonth from '@/components/TimelineMonth.vue';
import TimelineEventComponent from '@/components/TimelineEvent.vue';
import ErrorBoundary from '@/components/ErrorBoundary.vue';
import TimelineSkeleton from '@/components/TimelineSkeleton.vue';
import { Subscription } from 'rxjs';
import _ from 'lodash';
import { isSameMonth } from '@/utils/timeline.utils';

@Options({
  components: {
    TimelineMonth,
    TimelineEvent: TimelineEventComponent,
    ErrorBoundary,
    TimelineSkeleton,
  },
})
export default class RoadmapView extends Vue {
  private roadmapData: RoadmapData | null = null;
  private loading = false;
  private error: any = null;
  private selectedEvent: TimelineEvent | null = null;
  private selectedMonth: Date | null = null;
  private retryCount = 0;
  private maxRetries = 3;
  private ignoreNextDocumentClick = false;
  
  // Subscriptions
  private roadmapDataSub: Subscription | null = null;
  private loadingSub: Subscription | null = null;
  private errorSub: Subscription | null = null;
  
  // Scroll state
  private isAtStart = true;
  private isAtEnd = false;
  public scrollPosition = 0; // Make it public so Vue can track it
  private debouncedUpdateHiddenEvents: (() => void) | null = null;
  private debouncedHandleResize: (() => void) | null = null;

  async mounted(): Promise<void> {
    // Subscribe to service observables
    this.roadmapDataSub = roadmapService.getRoadmapData().subscribe((data) => {
      this.roadmapData = data;
      // Set up scroll listener after data is loaded
      if (data) {
        this.$nextTick(() => {
          this.setupScrollListener();
        });
      }
    });

    this.loadingSub = roadmapService.getLoadingState().subscribe((loading) => {
      this.loading = loading;
    });

    this.errorSub = roadmapService.getErrorState().subscribe((error) => {
      this.error = error;
    });

    // Set up debounced functions first
    this.debouncedUpdateHiddenEvents = _.debounce(this.updateScrollPosition, 100);
    this.debouncedHandleResize = _.debounce(this.handleResize, 250);
    
    // Add non-scroll listeners
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.debouncedHandleResize);

    // Initial data fetch
    await this.fetchData();
  }

  setupScrollListener(): void {
    // Try multiple times with delays since the element might be inside ErrorBoundary
    const trySetup = (attempt: number) => {
      const scrollElement = this.$refs.timelineScroll as HTMLElement;
      
      if (scrollElement) {
        scrollElement.addEventListener('scroll', this.handleScroll);
        
        // Set initial scroll position
        this.updateScrollPosition();

        setTimeout(() => this.$forceUpdate(), 100);
        

      } else if (attempt < 10) {
        setTimeout(() => trySetup(attempt + 1), attempt * 100);
      } else {
        console.error('Timeline scroll element not found after 5 attempts!');
      }
    };
    
    trySetup(1);
  }

  unmounted(): void {
    // Clean up subscriptions
    this.roadmapDataSub?.unsubscribe();
    this.loadingSub?.unsubscribe();
    this.errorSub?.unsubscribe();

    // Remove scroll listener
    const scrollElement = this.$refs.timelineScroll as HTMLElement;
    if (scrollElement) {
      scrollElement.removeEventListener('scroll', this.handleScroll);
    }
    
    // Remove document click listener
    document.removeEventListener('click', this.handleDocumentClick);
    
    // Remove keyboard listener
    document.removeEventListener('keydown', this.handleKeydown);
    
    // Remove resize listener
    if (this.debouncedHandleResize) {
      window.removeEventListener('resize', this.debouncedHandleResize);
    }
  }

  async fetchData(): Promise<void> {
    try {
      await roadmapService.fetchRoadmapData();
      this.retryCount = 0; // Reset retry count on success
    } catch (error) {
      // Error is handled by the service and observable
      console.error('Failed to fetch roadmap data:', error);
    }
  }

  async handleRetry(): Promise<void> {
    if (this.retryCount >= this.maxRetries) {
      console.warn('Maximum retry attempts reached');
      return;
    }
    
    this.retryCount++;
    await this.fetchData();
  }

  handleErrorBoundaryError(error: Error, errorInfo: any): void {
    console.error('ErrorBoundary caught error:', error, errorInfo);
    // Could send to error reporting service here
  }

  async refreshData(): Promise<void> {
    this.selectedEvent = null;
    await this.fetchData();
  }

  selectEvent(event: TimelineEvent): void {
    this.selectedEvent = this.selectedEvent?.id === event.id ? null : event;
  }

  closeEventModal(): void {
    this.selectedEvent = null;
  }

  handleMonthClick(month: Date): void {
    if (this.selectedMonth && this.isSameMonth(month, this.selectedMonth)) {
      // Clicking the same month closes it
      this.selectedMonth = null;
    } else {
      // Select new month
      this.selectedMonth = month;
      
      // Prevent the next document click from closing the popup immediately
      this.ignoreNextDocumentClick = true;
      setTimeout(() => {
        this.ignoreNextDocumentClick = false;
      }, 100);
    }
  }

  closeMonthPopup(): void {
    this.selectedMonth = null;
  }

  isSameMonth(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() && 
           date1.getMonth() === date2.getMonth();
  }

  selectEventFromMonth(event: TimelineEvent): void {
    this.selectedEvent = event;
    this.selectedMonth = null; // Close month popup when selecting event
  }

  getSelectedMonthName(): string {
    if (!this.selectedMonth) return '';
    return this.selectedMonth.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  }

  getMonthPopupStyle(): Record<string, string> {
    if (!this.selectedMonth || !this.roadmapData) return {};

    // Find the month index to calculate position
    let monthIndex = 0;
    let found = false;
    
    for (const quarter of this.roadmapData.quarters) {
      for (const month of quarter.months) {
        if (this.isSameMonth(month, this.selectedMonth)) {
          found = true;
          break;
        }
        monthIndex++;
      }
      if (found) break;
    }

    // Calculate position similar to event positioning
    const totalWidth = this.timelineWidth;
    const timelinePadding = 20;
    const availableWidth = totalWidth - (timelinePadding * 2);
    const totalMonths = this.roadmapData.quarters.reduce((total, quarter) => total + quarter.months.length, 0);
    const monthWidth = availableWidth / totalMonths;
    
    const pixelPosition = timelinePadding + (monthIndex * monthWidth) + (monthWidth / 2);
    const percentagePosition = (pixelPosition / totalWidth) * 100;

    // Ensure popup doesn't go off-screen
    const clampedPosition = Math.max(15, Math.min(85, percentagePosition));
    
    return {
      left: `${clampedPosition}%`,
      transform: 'translateX(-50%)',
      top: 'calc(50% + 45px)' // Position directly below the month blocks (50% center + 30px month height + 15px spacing)
    };
  }

  navigateEvent(direction: 'prev' | 'next'): void {
    if (!this.roadmapData || !this.selectedEvent) return;
    
    const currentIndex = this.selectedEventIndex;
    let newIndex: number;
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : this.roadmapData.events.length - 1;
    } else {
      newIndex = currentIndex < this.roadmapData.events.length - 1 ? currentIndex + 1 : 0;
    }
    
    this.selectedEvent = this.roadmapData.events[newIndex];
  }

  getEventStyle(event: TimelineEvent): Record<string, string> {
    if (!this.roadmapData) return {};

    // Find which month this event belongs to by counting from the start
    const eventDate = new Date(event.date);
    const eventYear = eventDate.getFullYear();
    const eventMonth = eventDate.getMonth(); // 0-based (0 = January)
    
    // Find the month index in our timeline
    let monthIndex = 0;
    let found = false;
    
    for (const quarter of this.roadmapData.quarters) {
      for (const month of quarter.months) {
        const monthDate = new Date(month);
        if (monthDate.getFullYear() === eventYear && monthDate.getMonth() === eventMonth) {
          found = true;
          break;
        }
        monthIndex++;
      }
      if (found) break;
    }
    
    // Calculate day position within the month (0-1)
    const dayOfMonth = eventDate.getDate();
    const daysInMonth = new Date(eventYear, eventMonth + 1, 0).getDate();
    let dayProgress = (dayOfMonth - 1) / (daysInMonth - 1);
    
    // Clamp day progress to keep dots well within month blocks
    dayProgress = Math.max(0.1, Math.min(0.75, dayProgress));
    
    // Timeline has 20px padding on each side, so available width is timeline width - 40px
    const totalWidth = this.timelineWidth;
    const timelinePadding = 20;
    const availableWidth = totalWidth - (timelinePadding * 2);
    
    // Get total number of months
    const totalMonths = this.roadmapData.quarters.reduce((total, quarter) => total + quarter.months.length, 0);
    
    // TimelineMonth components use flex: 1, so they share available space equally
    // Each month gets: availableWidth / totalMonths
    const monthWidth = availableWidth / totalMonths;
    
    // Calculate position: timeline padding + (month index * month width) + (day progress * month width)
    const pixelPosition = timelinePadding + (monthIndex * monthWidth) + (dayProgress * monthWidth);
    const percentagePosition = (pixelPosition / totalWidth) * 100;
    
    // Clamp to reasonable bounds
    const paddedPosition = Math.max(2, Math.min(98, percentagePosition));

    return {
      left: `${paddedPosition}%`,
      transform: `translateX(-50%) ${event.position === 'above' ? 'translateY(-100%)' : 'translateY(0)'}`,
      position: 'absolute'
    };
  }

  getEventPixelPosition(event: TimelineEvent): number {
    if (!this.roadmapData) return 0;

    // Find which month this event belongs to by counting from the start
    const eventDate = new Date(event.date);
    const eventYear = eventDate.getFullYear();
    const eventMonth = eventDate.getMonth(); // 0-based (0 = January)
    
    // Find the month index in our timeline
    let monthIndex = 0;
    let found = false;
    
    for (const quarter of this.roadmapData.quarters) {
      for (const month of quarter.months) {
        const monthDate = new Date(month);
        if (monthDate.getFullYear() === eventYear && monthDate.getMonth() === eventMonth) {
          found = true;
          break;
        }
        monthIndex++;
      }
      if (found) break;
    }
    
    // Calculate day position within the month (0-1)
    const dayOfMonth = eventDate.getDate();
    const daysInMonth = new Date(eventYear, eventMonth + 1, 0).getDate();
    let dayProgress = (dayOfMonth - 1) / (daysInMonth - 1);
    
    // Clamp day progress to keep dots well within month blocks
    dayProgress = Math.max(0.1, Math.min(0.75, dayProgress));
    
    // Timeline has 20px padding on each side, so available width is timeline width - 40px
    const totalWidth = this.timelineWidth;
    const timelinePadding = 20;
    const availableWidth = totalWidth - (timelinePadding * 2);
    
    // Get total number of months
    const totalMonths = this.roadmapData.quarters.reduce((total, quarter) => total + quarter.months.length, 0);
    
    // TimelineMonth components use flex: 1, so they share available space equally
    // Each month gets: availableWidth / totalMonths
    const monthWidth = availableWidth / totalMonths;
    
    // Calculate position: timeline padding + (month index * month width) + (day progress * month width)
    return timelinePadding + (monthIndex * monthWidth) + (dayProgress * monthWidth);
  }

  get timelineWidth(): number {
    if (!this.roadmapData) return 0;
    
    // Use a minimum width that ensures proper spacing
    const containerWidth = window.innerWidth || 1200;
    const minTimelineWidth = Math.max(containerWidth * 1.5, 1800);
    
    return minTimelineWidth;
  }

  formatEventDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatShortDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  get selectedEventIndex(): number {
    if (!this.roadmapData || !this.selectedEvent) return -1;
    return this.roadmapData.events.findIndex(event => event.id === this.selectedEvent?.id);
  }

  get canNavigatePrev(): boolean {
    return this.roadmapData ? this.roadmapData.events.length > 1 : false;
  }

  get canNavigateNext(): boolean {
    return this.roadmapData ? this.roadmapData.events.length > 1 : false;
  }

  get debugHiddenCount(): number {
    console.log('=== DEBUG HIDDEN COUNT CALLED ===');
    console.log('Scroll position:', this.scrollPosition);
    console.log('Has roadmap data:', !!this.roadmapData);
    console.log('Has events:', !!(this.roadmapData && this.roadmapData.events));
    
    if (this.roadmapData && this.roadmapData.events) {
      console.log('Event count:', this.roadmapData.events.length);
      return Math.floor(this.scrollPosition / 100); // Return scroll position divided by 100
    }
    return 0;
  }

  getHiddenEventsRight(): number {
   if (!this.roadmapData || !this.roadmapData.events) return 0;
    
    const scrollElement = this.$refs.timelineScroll as HTMLElement;
    if (!scrollElement) return 0;
    
    const scrollLeft = this.scrollPosition;
    const clientWidth = scrollElement.clientWidth;
    const viewportRight = scrollLeft + clientWidth;
    
    // Count events that are positioned to the right of the visible area
    const hiddenEvents = this.roadmapData.events.filter(event => {
      const eventPosition = this.getEventPixelPosition(event);
      const isHidden = eventPosition > viewportRight;
      //console.log(`Event "${event.title}": position=${eventPosition}, viewportRight=${viewportRight}, hidden=${isHidden}`);
      return isHidden;
    });

    return hiddenEvents.length;
  }

  getHiddenEventsLeft(): number {
    if (!this.roadmapData || !this.roadmapData.events) return 0;
    
    const scrollElement = this.$refs.timelineScroll as HTMLElement;
    if (!scrollElement) return 0;
    
    const scrollLeft = this.scrollPosition;
    const viewportLeft = scrollLeft;
    
    // Count events that are positioned to the left of the visible area
    const hiddenEvents = this.roadmapData.events.filter(event => {
      const eventPosition = this.getEventPixelPosition(event);
      const isHidden = eventPosition < viewportLeft;
      //console.log(`Event "${event.title}": position=${eventPosition}, viewportLeft=${viewportLeft}, hidden=${isHidden}`);
      return isHidden;
    });
    
    return hiddenEvents.length;
  }

  get hiddenEventsRight(): number {
    if (!this.roadmapData || !this.roadmapData.events) return 0;
    
    const scrollElement = this.$refs.timelineScroll as HTMLElement;
    if (!scrollElement) return 0;
    
    const scrollLeft = this.scrollPosition;
    const clientWidth = scrollElement.clientWidth;
    const viewportRight = scrollLeft + clientWidth;
    
    // Count events that are positioned to the right of the visible area
    const hiddenEvents = this.roadmapData.events.filter(event => {
      const eventPosition = this.getEventPixelPosition(event);
      const isHidden = eventPosition > viewportRight;
      return isHidden;
    });

    return hiddenEvents.length;
  }

  get monthEvents(): TimelineEvent[] {
    if (!this.selectedMonth || !this.roadmapData) return [];
    
    return this.roadmapData.events
      .filter(event => {
        const eventDate = new Date(event.date);
        return this.isSameMonth(eventDate, this.selectedMonth!);
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }

  scrollToStart(): void {
    const scrollElement = this.$refs.timelineScroll as HTMLElement;
    if (scrollElement) {
      scrollElement.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }

  scrollToEnd(): void {
    const scrollElement = this.$refs.timelineScroll as HTMLElement;
    if (scrollElement) {
      scrollElement.scrollTo({ 
        left: scrollElement.scrollWidth - scrollElement.clientWidth, 
        behavior: 'smooth' 
      });
    }
  }

  scrollLeft(): void {
    const scrollElement = this.$refs.timelineScroll as HTMLElement;
    if (!scrollElement || !this.roadmapData || !this.roadmapData.events) return;

    const currentScrollLeft = scrollElement.scrollLeft;
    const viewportLeft = currentScrollLeft;
    
    // Find the nearest event to the left of the current viewport
    const eventsToLeft = this.roadmapData.events
      .map(event => ({
        event,
        position: this.getEventPixelPosition(event)
      }))
      .filter(item => item.position < viewportLeft)
      .sort((a, b) => b.position - a.position); // Sort by position descending (nearest first)
    
    if (eventsToLeft.length > 0) {
      // Scroll to show the nearest event on the left, centered in viewport
      const targetEvent = eventsToLeft[0];
      const targetScrollLeft = Math.max(0, targetEvent.position - scrollElement.clientWidth / 2);
      
      scrollElement.scrollTo({ 
        left: targetScrollLeft, 
        behavior: 'smooth' 
      });
    } else {
      // No events to the left, scroll to the beginning
      scrollElement.scrollTo({ 
        left: 0, 
        behavior: 'smooth' 
      });
    }
  }

  scrollRight(): void {
    const scrollElement = this.$refs.timelineScroll as HTMLElement;
    if (!scrollElement || !this.roadmapData || !this.roadmapData.events) return;

    const currentScrollLeft = scrollElement.scrollLeft;
    const viewportRight = currentScrollLeft + scrollElement.clientWidth;
    
    // Find the nearest event to the right of the current viewport
    const eventsToRight = this.roadmapData.events
      .map(event => ({
        event,
        position: this.getEventPixelPosition(event)
      }))
      .filter(item => item.position > viewportRight)
      .sort((a, b) => a.position - b.position); // Sort by position ascending (nearest first)
    
    if (eventsToRight.length > 0) {
      // Scroll to show the nearest event on the right, centered in viewport
      const targetEvent = eventsToRight[0];
      const targetScrollLeft = Math.max(0, targetEvent.position - scrollElement.clientWidth / 2);
      
      scrollElement.scrollTo({ 
        left: targetScrollLeft, 
        behavior: 'smooth' 
      });
    } else {
      // No events to the right, scroll to the end
      scrollElement.scrollTo({ 
        left: scrollElement.scrollWidth - scrollElement.clientWidth, 
        behavior: 'smooth' 
      });
    }
  }

  handleScroll(): void {
    const scrollElement = this.$refs.timelineScroll as HTMLElement;
    if (!scrollElement) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollElement;
    
    this.isAtStart = scrollLeft <= 10;
    this.isAtEnd = scrollLeft >= scrollWidth - clientWidth - 10;
    
    // Update scroll position immediately
    this.scrollPosition = scrollLeft;
    
    // Force Vue to update computed properties
    this.$forceUpdate();
    
    // Also call debounced version
    if (this.debouncedUpdateHiddenEvents) {
      this.debouncedUpdateHiddenEvents();
    }
  }

  updateScrollPosition(): void {
    const scrollElement = this.$refs.timelineScroll as HTMLElement;
    if (scrollElement) {
      this.scrollPosition = scrollElement.scrollLeft;
    }
  }

  handleDocumentClick(event: Event): void {
    // Close month popup when clicking outside
    if (this.ignoreNextDocumentClick) {
      return;
    }
    
    if (this.selectedMonth) {
      this.selectedMonth = null;
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    // Only handle keyboard events when timeline is focused or no modal is open
    if (this.selectedEvent) return;
    
    const scrollElement = this.$refs.timelineScroll as HTMLElement;
    if (!scrollElement) return;

    switch (event.key) {
      case 'ArrowLeft':
        if (!this.isAtStart) {
          event.preventDefault();
          this.scrollLeft();
        }
        break;
      case 'ArrowRight':
        if (!this.isAtEnd) {
          event.preventDefault();
          this.scrollRight();
        }
        break;
      case 'Home':
        event.preventDefault();
        this.scrollToStart();
        break;
      case 'End':
        event.preventDefault();
        this.scrollToEnd();
        break;
    }
  }

  handleResize(): void {
    // Force update to recalculate scroll indicators on window resize
    this.$forceUpdate();
  }

  getErrorTypeColor(type: string): string {
    switch (type) {
      case 'network': return 'orange';
      case 'server': return 'red';
      case 'data': return 'purple';
      default: return 'grey';
    }
  }

  getErrorTypeLabel(type: string): string {
    switch (type) {
      case 'network': return 'Network Issue';
      case 'server': return 'Server Error';
      case 'data': return 'Data Issue';
      default: return 'Unknown Error';
    }
  }
}
